import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate, NavLink } from "react-router-dom";
import { Menu, LogOut, Bell, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { useAuth } from "@/context/AuthContext";
import { useI18n, useL } from "@/context/I18nContext";
import { navLabel } from "@/data/nav";
import api from "@/lib/api";

const ROLE_LABEL = {
  client: ["Cliente", "Client", "Kunde"],
  specialist: ["Specialista", "Specialist", "Spezialist"],
  admin: ["Amministratore", "Administrator", "Administrator"],
  super_admin: ["Super Admin", "Super Admin", "Super-Admin"],
};

function SidebarNav({ nav, onNavigate, lang }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`sidebar-${item.to}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
              isActive
                ? "bg-cyanx/10 text-cyanx font-medium border border-cyanx/20"
                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`
          }
        >
          {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
          <span className="truncate">{navLabel(item, lang)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout({ nav, title }) {
  const { user, logout } = useAuth();
  const { lang } = useI18n();
  const L = useL();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    api.get("/notifications").then(({ data }) => active && setUnread(data.unread || 0)).catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  const current = nav.find((n) => location.pathname === n.to) ||
    nav.filter((n) => location.pathname.startsWith(n.to + "/")).sort((a, b) => b.to.length - a.to.length)[0];
  const shellTitle = L(title.it, title.en, title.de);
  const pageTitle = current ? navLabel(current, lang) : shellTitle;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-navy text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/10 bg-navy-paper fixed inset-y-0 z-40">
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto py-5">
          <p className="px-6 mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">{shellTitle}</p>
          <SidebarNav nav={nav} lang={lang} />
        </div>
      </aside>

      <div className="flex-1 lg:pl-64 min-w-0">
        {/* Topbar */}
        <header className="h-16 sticky top-0 z-30 rc-glass border-b border-white/10 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10" data-testid="dashboard-menu-trigger">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-navy-paper border-white/10 p-0">
                <div className="h-16 flex items-center px-5 border-b border-white/10"><Logo /></div>
                <div className="py-5"><SidebarNav nav={nav} lang={lang} onNavigate={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <h1 className="font-heading text-lg font-medium truncate">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Link to={user?.role === "client" ? "/portal/notifications" : "#"} className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:bg-white/5" data-testid="notif-bell">
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-gold text-navy text-[10px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-md border border-white/10 px-2.5 h-9 hover:bg-white/5" data-testid="user-menu">
                  <span className="h-6 w-6 rounded-full bg-cyanx/20 text-cyanx flex items-center justify-center text-xs font-semibold">
                    {(user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase()}
                  </span>
                  <span className="hidden sm:inline text-sm text-slate-200 max-w-[120px] truncate">{user?.first_name || user?.email}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm truncate">{user?.email}</div>
                  <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role] && L(...ROLE_LABEL[user.role])}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/">{L("Vai al sito pubblico", "Go to public site", "Zur öffentlichen Website")}</Link></DropdownMenuItem>
                {user?.role === "client" && <DropdownMenuItem asChild><Link to="/portal/profile">{L("Profilo", "Profile", "Profil")}</Link></DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-400">
                  <LogOut className="h-4 w-4 mr-2" />{L("Esci", "Log out", "Abmelden")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
